import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { threatcatService } from '@/services/threatcatService';
import { ThreatCatAuthError } from '@/lib/errors';

interface ConnectModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: () => void | Promise<void>;
}

export default function ConnectModal({ open, onClose, onConnected }: ConnectModalProps) {
  const { connect, disconnect, cookie: savedCookie } = useThreatCat();
  const [cookie, setCookie] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    const trimmed = cookie.trim();
    if (!trimmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const projects = await threatcatService.fetchThreatCatProjects(trimmed);
      await connect(trimmed, projects);
      await onConnected?.();
      setCookie('');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (err instanceof ThreatCatAuthError) {
        setError('Invalid ThreatCat cookie. Go back to ThreatCat, log in again, and copy the updated value.');
      } else {
        setError(`Could not connect to ThreatCat: ${message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConnect();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Refresh ThreatCat project catalog</DialogTitle>
          <DialogDescription>
            Refresh your local project catalog with your ThreatCat session cookie.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">How to get the cookie:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Open <strong>ThreatCat</strong> and log in</li>
              <li>Press F12 → <strong>Application</strong> tab → <strong>Cookies</strong></li>
              <li>Find the <code className="bg-muted px-1 rounded">__Host-_oauth2_proxy</code> cookie</li>
              <li>Copy the full value and paste it here</li>
            </ol>
          </div>

          <div className="space-y-2">
            <label htmlFor="cookie-input" className="text-sm font-medium">
              Cookie <code className="bg-muted px-1 rounded">__Host-_oauth2_proxy</code>
            </label>
            <Textarea
              id="cookie-input"
              value={cookie}
              onChange={(e) => setCookie(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Paste the cookie value here..."
              className="font-mono text-xs h-24 resize-none"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="flex justify-between items-center gap-2">
            <div>
              {savedCookie && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    disconnect();
                    setCookie('');
                    setError(null);
                    onClose();
                  }}
                  disabled={isLoading}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  Clear saved cookie
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleConnect} disabled={isLoading || !cookie.trim()}>
                {isLoading ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent inline-block" />
                    Refreshing...
                  </>
                ) : (
                  'Refresh catalog'
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
